import React, { useContext, useCallback, useRef, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { useMutation } from "@apollo/client";
import { DELETE_AGENDAITEM_FILE_MANAGER } from "src/api/assembly";
import {
    DeleteAgendaItemFileManager,
    DeleteAgendaItemFileManagerVariables
} from "src/api/generated/DeleteAgendaItemFileManager";
import { MessageType } from "src/components/notifications/Notifier";
import { AgendaItemDocumentsListItem } from "./list-item/AgendaItemDocumentsListItem";
import styles from "./AgendaItemsListAllDocuments.module.css";
import { AgendaItem, AgendaItemChild } from "src/types";

type FlattenedItem = {
    item: AgendaItem | AgendaItemChild;
    parentSort?: number;
};

const AgendaItemsListAllDocumentsBase: React.FC = () => {
    const { t } = useTranslation();
    const { uiStore, agendaitemStore, assemblyStore } = useContext(RootStoreContext);
    const lastMutationVars = useRef<DeleteAgendaItemFileManagerVariables | null>(null);

    const printStatus = useCallback(
        (msg: string, type: MessageType) =>
            uiStore.enqueueSnackbar({
                content: msg,
                options: { type, autoClose: type === MessageType.ERROR ? false : 3000 }
            }),
        [uiStore]
    );

    const [deleteFile] = useMutation<DeleteAgendaItemFileManager, DeleteAgendaItemFileManagerVariables>(
        DELETE_AGENDAITEM_FILE_MANAGER,
        {
            onCompleted: () => {
                const fileToRemove = lastMutationVars.current?.fileid;
                if (fileToRemove) {
                    agendaitemStore.removeAgendaItemFile(fileToRemove, lastMutationVars.current?.agendaitemid);
                    printStatus(
                        t("screens.realestate.assembly.agendaitem_edit.remove_file_success"),
                        MessageType.SUCCESS
                    );
                }
            },
            onError: (error) => {
                printStatus(
                    t("screens.realestate.assembly.agendaitem_edit.remove_file_error") + error.message,
                    MessageType.ERROR
                );
            }
        }
    );

    const onDeleteAgendaItemFile = useCallback(
        (fileid: string, agendaitemId: string) => async (e: React.MouseEvent) => {
            e.preventDefault();
            if (!agendaitemId) {
                printStatus(t("screens.realestate.assembly.agendaitem_edit.no_current_agenda_item"), MessageType.ERROR);
                return;
            }
            lastMutationVars.current = { fileid, agendaitemid: agendaitemId };
            await deleteFile({ variables: lastMutationVars.current! });
        },
        [deleteFile, printStatus, t]
    );

    const baseUrl = NetworkConfig.datafileUrl;

    // 1) flatten parents + children
    const flatItems = useMemo<FlattenedItem[]>(
        () =>
            agendaitemStore.currentAgendaItems.flatMap((parent) => [
                { item: parent },
                ...(parent.agendaitemchild ?? []).map((child) => ({
                    item: child,
                    parentSort: parent.sort
                }))
            ]),
        [agendaitemStore.currentAgendaItems]
    );

    // 2) filter to only those with files
    const itemsWithFiles = useMemo(() => flatItems.filter(({ item }) => item.agendaitemfiles.length > 0), [flatItems]);

    // 3) split into two balanced columns by file count
    const [col1, col2] = useMemo(() => {
        const totalFiles = itemsWithFiles.reduce((sum, { item }) => sum + item.agendaitemfiles.length, 0);
        const half = totalFiles / 2;
        let cum = 0;
        let splitIndex = itemsWithFiles.length;

        for (let i = 0; i < itemsWithFiles.length; i++) {
            cum += itemsWithFiles[i].item.agendaitemfiles.length;
            if (cum >= half) {
                splitIndex = i + 1;
                break;
            }
        }

        return [itemsWithFiles.slice(0, splitIndex), itemsWithFiles.slice(splitIndex)];
    }, [itemsWithFiles]);

    // helper to render a list of items
    const renderColumn = (list: FlattenedItem[]) =>
        list.map(({ item, parentSort }) => {
            const prefix = parentSort != null ? `${parentSort}.${item.sort}.` : `${item.sort}.`;

            return (
                <div key={item.id} className={styles.AgendaItem}>
                    <div className={styles.AgendaItemTitle}>
                        {prefix} {item.text}
                    </div>
                    {item.agendaitemfiles.map((file) => (
                        <AgendaItemDocumentsListItem
                            key={file.fileid}
                            fileId={file.fileid}
                            fileName={file.name ?? ""}
                            fileLink={`${baseUrl}${file.fileid}`}
                            isCurrentAgendaItemDocument={false}
                            additionalIcon={
                                !assemblyStore.isAssemblyReadOnly && (
                                    <UI.Icon
                                        className={styles.TrashIcon}
                                        icon={UI.SVGIcon.Trash}
                                        onClick={onDeleteAgendaItemFile(file.fileid, item.id)}
                                    />
                                )
                            }
                        />
                    ))}
                </div>
            );
        });

    return (
        <div className={`${styles.AgendaItemsContainer} mb-4`}>
            <div className={styles.Column}>{renderColumn(col1)}</div>
            <div className={styles.Column}>{renderColumn(col2)}</div>
        </div>
    );
};

export const AgendaItemsListAllDocuments = Sentry.withProfiler(observer(AgendaItemsListAllDocumentsBase));
